import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLiveEventSocket } from "hooks";
import { userModule } from "constants";
import { MAX_LIVE_EVENTS } from "pages/LiveEventTracking/constant";
import LiveEventsContext, { LiveEventsBellContext } from "./LiveEventsContext";

const LiveEventsProvider = ({ children }) => {
  const userModules = useSelector((state) => state.userData?.modules);

  // Check if user has live event tracking access
  const hasAccess = useMemo(
    () =>
      Array.isArray(userModules) &&
      userModules.some(
        (module) => module?.name === userModule.liveEventTracking,
      ),
    [userModules],
  );

  const { isConnected, events, connectionError, clearEvents, latestLiveEvent } =
    useLiveEventSocket(MAX_LIVE_EVENTS, hasAccess);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Ref so the effect below doesn't re-run when the panel opens/closes.
  const isPanelOpenRef = useRef(isPanelOpen);
  isPanelOpenRef.current = isPanelOpen;

  // Increase unread count on new event when panel is closed.
  useEffect(() => {
    if (latestLiveEvent && !isPanelOpenRef.current) {
      setUnreadCount((count) => count + 1);
    }
  }, [latestLiveEvent]);

  // Opening the panel marks everything as read.
  useEffect(() => {
    if (isPanelOpen) setUnreadCount(0);
  }, [isPanelOpen]);

  const togglePanel = useCallback(() => setIsPanelOpen((open) => !open), []);

  // Full data for the page and panel.
  const value = useMemo(
    () => ({
      hasAccess,
      isConnected,
      events,
      connectionError,
      clearEvents,
      isPanelOpen,
    }),
    [hasAccess, isConnected, events, connectionError, clearEvents, isPanelOpen],
  );

  // Only what the header bell needs; unchanged when the panel is open and events arrive.
  const bellValue = useMemo(
    () => ({ hasAccess, isConnected, unreadCount, isPanelOpen, togglePanel }),
    [hasAccess, isConnected, unreadCount, isPanelOpen, togglePanel],
  );

  return (
    <LiveEventsBellContext.Provider value={bellValue}>
      <LiveEventsContext.Provider value={value}>
        {children}
      </LiveEventsContext.Provider>
    </LiveEventsBellContext.Provider>
  );
};

export default LiveEventsProvider;
